<template>
    <canvas ref="canvas" height="160" style="width: 100%;" />
</template>

<script>
const COLORS = {
    0: 'rgba(8, 8, 28, 0.97)',     // Night
    1: 'rgba(22, 30, 75, 0.93)',   // Astronomical
    2: 'rgba(55, 85, 165, 0.88)',  // Nautical
    3: 'rgba(215, 120, 45, 0.85)', // Civil
    4: 'rgba(245, 190, 55, 0.85)', // Day
}
const SEGMENT_LABELS = { 0: 'Night', 1: 'Astronomical', 2: 'Nautical', 3: 'Civil', 4: 'Day' }

function parseMinutes(t) {
    if (!t) return null
    const m = t.match(/(\d+):(\d+)\s*(am|pm)/i)
    if (!m) return null
    let h = parseInt(m[1])
    const min = parseInt(m[2])
    const ap = m[3].toLowerCase()
    if (ap === 'pm' && h !== 12) h += 12
    if (ap === 'am' && h === 12) h = 0
    return h * 60 + min
}

function shortTime(t) {
    if (!t) return ''
    const m = t.match(/(\d+):(\d+)\s*(am|pm)/i)
    if (!m) return t
    return `${parseInt(m[1])}:${m[2]}${m[3].toLowerCase()}`
}

function fmtDuration(min) {
    if (min < 60) return `${Math.round(min)}m`
    const h = Math.floor(min / 60)
    const r = Math.round(min % 60)
    return r ? `${h}h ${r}m` : `${h}h`
}

export default {
    name: 'TwilightBarChart',
    props: {
        twilights: { type: Array, default: () => [] },
        title:     { type: String, default: '' },
        subtitle:  { type: String, default: '' },
        subtitle2: { type: String, default: '' },
    },
    watch: {
        twilights() { this.$nextTick(this.redraw) },
    },
    mounted() {
        this.$nextTick(this.redraw)
        this._ro = new ResizeObserver(() => this.redraw())
        this._ro.observe(this.$el)
    },
    beforeDestroy() {
        if (this._ro) this._ro.disconnect()
    },
    methods: {
        redraw() {
            const canvas = this.$refs.canvas
            if (!canvas || this.twilights.length < 8) return

            // Group by type; sort pairs so AM (earlier) is always index 0
            const byType = {}
            for (const t of this.twilights) {
                if (!byType[t.type]) byType[t.type] = []
                byType[t.type].push(t)
            }
            const sorted = tp => (byType[tp] || []).slice().sort((a, b) => (parseMinutes(a.time) || 0) - (parseMinutes(b.time) || 0))
            const night              = byType[0] && byType[0][0]
            const [astroAM, astroPM] = sorted(1)
            const [nautAM,  nautPM]  = sorted(2)
            const [civilAM, civilPM] = sorted(3)
            const day                = byType[4] && byType[4][0]
            if (!night || !astroAM || !astroPM || !nautAM || !nautPM || !civilAM || !civilPM || !day) return

            const dpr  = window.devicePixelRatio || 1
            const cssW = Math.round(((canvas.parentElement ? canvas.parentElement.clientWidth : 0) || 600) * 0.9)
            const cssH = 160
            canvas.width  = cssW * dpr
            canvas.height = cssH * dpr
            canvas.style.height = cssH + 'px'
            canvas.style.width  = cssW + 'px'
            const ctx = canvas.getContext('2d')
            ctx.scale(dpr, dpr)
            const w = cssW, h = cssH
            // snap to nearest physical pixel to keep segment edges sharp
            const snap = v => Math.round(v * dpr) / dpr

            const PAD_T = 60, PAD_B = 48, PAD_L = 16, PAD_R = 16
            const plotW = w - PAD_L - PAD_R
            const BAR_H = 32
            const barTop = PAD_T + ((h - PAD_T - PAD_B) - BAR_H) / 2
            const barBot = barTop + BAR_H

            ctx.clearRect(0, 0, w, h)

            if (this.title) {
                ctx.font = 'bold 11px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.6)'
                ctx.fillText(this.title, PAD_L, 16)
            }
            if (this.subtitle) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle, PAD_L, 28)
            }
            if (this.subtitle2) {
                ctx.font = '10px system-ui, sans-serif'
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.38)'
                ctx.fillText(this.subtitle2, PAD_L, 40)
            }

            // Night is split across midnight: nightStart = minutes from 00:00 to first astro twilight,
            // nightEnd = remainder so nightStart + nightEnd = night.duration_minutes.
            const nightStart = parseMinutes(astroAM.time) || 0
            const nightEnd   = Math.max(0, night.duration_minutes - nightStart)

            const segments = [
                { dur: nightStart,               type: 0 },
                { dur: astroAM.duration_minutes,  type: 1 },
                { dur: nautAM.duration_minutes,   type: 2 },
                { dur: civilAM.duration_minutes,  type: 3 },
                { dur: day.duration_minutes,      type: 4 },
                { dur: civilPM.duration_minutes,  type: 3 },
                { dur: nautPM.duration_minutes,   type: 2 },
                { dur: astroPM.duration_minutes,  type: 1 },
                { dur: nightEnd,                 type: 0 },
            ]
            const total = segments.reduce((s, g) => s + g.dur, 0) || 1440

            // Segment left-edge X positions
            const edgeX = []
            let elapsed = 0
            for (const seg of segments) {
                edgeX.push(PAD_L + (elapsed / total) * plotW)
                elapsed += seg.dur
            }
            edgeX.push(PAD_L + plotW) // right edge

            // Draw segments
            ctx.font = 'bold 9px system-ui, sans-serif'
            for (let i = 0; i < segments.length; i++) {
                const seg  = segments[i]
                const segX = snap(edgeX[i])
                const segW = snap(edgeX[i + 1]) - segX
                ctx.fillStyle = COLORS[seg.type]
                ctx.fillRect(segX, barTop, segW, BAR_H)

                // Duration label inside segment if it fits
                const label = fmtDuration(seg.dur)
                const tw = ctx.measureText(label).width
                if (tw < segW - 6) {
                    ctx.fillStyle = seg.type >= 3 ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.85)'
                    ctx.textAlign = 'center'
                    ctx.fillText(label, segX + segW / 2, barTop + BAR_H / 2 + 3)
                }
            }

            // Bar outline — offset by 0.5 CSS px so 1px stroke lands on a pixel boundary
            ctx.strokeStyle = 'rgba(0,0,0,0.15)'
            ctx.lineWidth = 1
            ctx.strokeRect(PAD_L + 0.5, barTop + 0.5, plotW - 1, BAR_H - 1)

            // X-axis labels at key boundaries with collision avoidance
            const labelPts = [
                { x: edgeX[1], label: shortTime(astroAM.time) },  // astronomical dawn
                { x: edgeX[4], label: shortTime(day.time) },       // sunrise
                { x: edgeX[5], label: shortTime(civilPM.time) },   // sunset
                { x: edgeX[8], label: shortTime(night.time) },     // night begins
            ]

            ctx.font = '9px system-ui, sans-serif'
            ctx.fillStyle = 'rgba(0,0,0,0.45)'
            let lastLabelX = -Infinity
            for (const pt of labelPts) {
                if (pt.x - lastLabelX < 40) continue
                ctx.textAlign = 'center'
                ctx.fillText(pt.label, pt.x, barBot + 13)
                lastLabelX = pt.x
            }

            // Legend centered below x-axis labels
            ctx.font = '10px system-ui, sans-serif'
            const SW = 14, SH = 8, GAP = 14
            const items = [0, 1, 2, 3, 4].map(type => ({
                type,
                label: SEGMENT_LABELS[type],
                lw: ctx.measureText(SEGMENT_LABELS[type]).width,
            }))
            let totalLW = items.reduce((s, it, i) => s + SW + 6 + it.lw + (i < 4 ? GAP : 0), 0)
            let lx = Math.max(PAD_L, (w - totalLW) / 2)
            const legendY = h - 8

            for (const item of items) {
                ctx.fillStyle = COLORS[item.type]
                ctx.fillRect(lx, legendY - SH, SW, SH)
                ctx.strokeStyle = 'rgba(0,0,0,0.12)'
                ctx.lineWidth = 1
                ctx.strokeRect(lx, legendY - SH, SW, SH)
                lx += SW + 6
                ctx.textAlign = 'left'
                ctx.fillStyle = 'rgba(0,0,0,0.5)'
                ctx.fillText(item.label, lx, legendY)
                lx += item.lw + GAP
            }
        },
    },
}
</script>
